"use client";

import { Link } from "@ui/components/core/Link";
import { BackButton } from "@ui/components/demos/VideoToMusic/components/BackButton";
import { MinimalAudioPlayer } from "@ui/components/demos/VideoToMusic/components/MinimalAudioPlayer";
import { PremiumLoadingIndicator } from "@ui/components/demos/VideoToMusic/components/PremiumLoadingIndicator";
import { StudioCTAButton } from "@ui/components/demos/VideoToMusic/components/StudioCTAButton";
import { VideoPreview } from "@ui/components/demos/VideoToMusic/components/VideoPreview";
import { UploadButton } from "@ui/components/demos/VideoToMusic/components/VideoToMusicControls";
import { useVideoProcessing } from "@ui/components/demos/VideoToMusic/hooks/useVideoProcessing";
import type { PlayingState, SoundtrackOptions } from "@ui/components/demos/VideoToMusic/types";
import { logger } from "@utilities/logger";
import { useTranslations } from "next-intl";
import { useCallback, useRef, useState } from "react";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { publicUrls } from "@/constants";

export interface VideoToMusicProps {
  size?: "2xl" | "md" | "xl";
}

export function VideoToMusic({ size: _size = "md" }: VideoToMusicProps = {}) {
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const videoElementRef = useRef<HTMLVideoElement>(null);

  const t = useTranslations("ClientKeys.VideoToMusic");

  // State
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<null | string>(null);
  const [generatedAudio, setGeneratedAudio] = useState<null | string>(null);
  const [playingState, setPlayingState] = useState<PlayingState>("idle");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>("");
  const [isRateLimitError, setIsRateLimitError] = useState<boolean>(false);
  const [syncController, setSyncController] = useState<"audio" | "video" | null>(null);
  const [loadingPresetVideoSrc, setLoadingPresetVideoSrc] = useState<null | string>(null);
  const lastSyncTimeRef = useRef<number>(0);

  // Hooks
  const { processVideo } = useVideoProcessing();

  // Actions
  const handleGenerateMusic = useCallback(
    async (file: File = videoFile!) => {
      if (!file) return;

      try {
        setError("");
        setIsRateLimitError(false);
        setIsProcessing(true);
        setPlayingState("loading");

        // Use default soundtrack options (background music only)
        const soundtrackOptions: SoundtrackOptions = {
          backgroundMusic: true,
          soundEffects: false,
          voiceOver: false,
          mood: "",
        };

        const result = await processVideo(file, soundtrackOptions);

        captureEvent(EventName.marketing_website_video_to_music_generate);

        // Clean up previous audio URL
        if (generatedAudio) {
          URL.revokeObjectURL(generatedAudio);
        }

        // Convert base64 audio to blob URL
        const audioBlob = new Blob([Uint8Array.from(atob(result.audioData), c => c.charCodeAt(0))], {
          type: "audio/wav",
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        setGeneratedAudio(audioUrl);

        // Auto-play the generated audio and sync video
        if (audioElementRef.current) {
          audioElementRef.current.src = audioUrl;
          audioElementRef.current.play();

          // Also start the video in sync
          if (videoElementRef.current) {
            videoElementRef.current.currentTime = 0;
            videoElementRef.current.play().catch(() => {
              // Video autoplay might be blocked, that's okay

              logger.warn("Video autoplay was blocked by browser");
            });
          }
        }

        setPlayingState("playing");
      } catch (err) {
        logger.error(err instanceof Error ? err : null, "Failed to generate music");

        // Check if this is a rate limit error
        if (
          err instanceof Error &&
          "isRateLimited" in err &&
          (err as Error & { isRateLimited: boolean }).isRateLimited
        ) {
          setIsRateLimitError(true);
          setError(t("rate_limit_error_message"));
        } else {
          const errorMessage = err instanceof Error ? err.message : "Failed to generate music. Please try again.";
          setError(errorMessage);
        }
        setPlayingState("idle");
      } finally {
        setIsProcessing(false);
      }
    },
    [videoFile, processVideo, generatedAudio, t],
  );

  const handleFileAccepted = useCallback(
    async (file: File) => {
      setVideoFile(file);
      setError("");
      setIsRateLimitError(false);

      // Create video URL
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
      const newVideoUrl = URL.createObjectURL(file);
      setVideoUrl(newVideoUrl);

      // Immediately start generating music
      await handleGenerateMusic(file);
    },
    [videoUrl, handleGenerateMusic],
  );

  const handlePresetVideoSelected = useCallback(
    async (presetVideoSrc: string, _description: string) => {
      try {
        setError("");
        setIsRateLimitError(false);
        setLoadingPresetVideoSrc(presetVideoSrc);

        // Fetch the preset video as a blob
        const response = await fetch(presetVideoSrc);
        const blob = await response.blob();

        // Create a File object from the blob
        const fileName = presetVideoSrc.split("/").pop() || "preset-video.mp4";
        const file = new File([blob], fileName, { type: blob.type || "video/mp4" });

        // Process the file like a regular upload
        await handleFileAccepted(file);
      } catch (err) {
        logger.error(err instanceof Error ? err : null, "Failed to load preset video");
        setError("Failed to load preset video. Please try again.");
        setIsProcessing(false);
      } finally {
        setLoadingPresetVideoSrc(null);
      }
    },
    [handleFileAccepted],
  );

  const handlePlay = useCallback(() => {
    if (!audioElementRef.current || !generatedAudio) return;

    const audio = audioElementRef.current;
    const video = videoElementRef.current;

    switch (playingState) {
      case "idle":
      case "paused":
        audio.play();
        // Sync video playback - restart video when audio plays
        if (video) {
          video.currentTime = 0;
          video.play().catch(() => {
            // Video autoplay might be blocked, that's okay
          });
        }
        break;
      case "playing":
        audio.pause();
        // Pause video when audio pauses
        if (video) {
          video.pause();
        }
        break;
    }
  }, [playingState, generatedAudio]);

  const resetWidget = useCallback(() => {
    // Stop and clear audio playback
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
      audioElementRef.current.src = "";
    }

    // Stop and reset video playback
    if (videoElementRef.current) {
      videoElementRef.current.pause();
      videoElementRef.current.currentTime = 0;
    }

    // Clean up generated audio URL
    if (generatedAudio) {
      URL.revokeObjectURL(generatedAudio);
    }

    // Clean up video URL
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }

    // Reset all state
    setVideoFile(null);
    setVideoUrl(null);
    setGeneratedAudio(null);
    setError("");
    setIsRateLimitError(false);
    setPlayingState("idle");
    setIsProcessing(false);
    setSyncController(null);
    setLoadingPresetVideoSrc(null);
    lastSyncTimeRef.current = 0;
  }, [videoUrl, generatedAudio]);

  // Video event handlers for bidirectional sync
  const handleVideoSeeked = useCallback(() => {
    // When user scrubs/seeks video, sync audio to match
    if (videoElementRef.current && audioElementRef.current && generatedAudio) {
      setSyncController("video");
      const videoTime = videoElementRef.current.currentTime;
      audioElementRef.current.currentTime = videoTime;
      lastSyncTimeRef.current = Date.now();

      // Reset sync controller after a longer delay to prevent conflicts
      setTimeout(() => setSyncController(null), 500);
    }
  }, [generatedAudio]);

  const handleVideoPlay = useCallback(() => {
    // When user plays video, also play audio
    if (audioElementRef.current && generatedAudio) {
      setSyncController("video");
      audioElementRef.current.play().catch(() => {
        // Audio might fail to play, that's okay
      });

      // Reset sync controller after a brief delay
      setTimeout(() => setSyncController(null), 300);
    }
  }, [generatedAudio]);

  const handleVideoPause = useCallback(() => {
    // When user pauses video, also pause audio
    if (audioElementRef.current && generatedAudio) {
      setSyncController("video");
      audioElementRef.current.pause();

      // Reset sync controller after a brief delay
      setTimeout(() => setSyncController(null), 300);
    }
  }, [generatedAudio]);

  const handleVideoTimeUpdate = useCallback(() => {
    // Handle case where music is longer than video and continuous sync
    if (videoElementRef.current && audioElementRef.current && generatedAudio && syncController !== "audio") {
      const now = Date.now();
      // Throttle sync operations to prevent choppy audio (max once per 250ms)
      if (now - lastSyncTimeRef.current < 250) return;

      const videoTime = videoElementRef.current.currentTime;
      const videoDuration = videoElementRef.current.duration;
      const audioTime = audioElementRef.current.currentTime;
      const audioDuration = audioElementRef.current.duration;

      // If video has ended but audio is still playing, let audio continue
      // and stop video at the end frame
      if (videoTime >= videoDuration && audioTime < audioDuration) {
        // Keep video at the last frame, don't loop if audio is longer
        videoElementRef.current.currentTime = videoDuration;
        return;
      }

      // Normal sync check - only sync if significantly out of sync
      const timeDiff = Math.abs(videoTime - audioTime);
      if (timeDiff > 0.3) {
        // Higher threshold to prevent choppy audio
        audioElementRef.current.currentTime = videoTime;
        lastSyncTimeRef.current = now;
      }
    }
  }, [generatedAudio, syncController]);

  const hasGeneratedAudio = !!generatedAudio;

  return (
    <>
      <audio
        className='hidden'
        onEnded={() => {
          setPlayingState("idle");
          // Stop video when audio ends
          if (videoElementRef.current) {
            videoElementRef.current.pause();
            videoElementRef.current.currentTime = 0;
          }
        }}
        onPause={() => {
          setPlayingState("paused");
          // Pause video when audio pauses
          if (videoElementRef.current) {
            videoElementRef.current.pause();
          }
        }}
        onPlay={() => {
          setPlayingState("playing");
          // Play video when audio plays
          if (videoElementRef.current) {
            videoElementRef.current.play().catch(() => {
              // Video autoplay might be blocked, that's okay
            });
          }
        }}
        onTimeUpdate={() => {
          // Keep video and audio in sync during playback (when audio is controlling)
          if (audioElementRef.current && videoElementRef.current && syncController !== "video") {
            const now = Date.now();
            // Throttle sync operations to prevent choppy audio (max once per 250ms)
            if (now - lastSyncTimeRef.current < 250) return;

            const audioTime = audioElementRef.current.currentTime;
            const videoTime = videoElementRef.current.currentTime;
            const videoDuration = videoElementRef.current.duration;
            const timeDiff = Math.abs(audioTime - videoTime);

            // If audio time is beyond video duration, keep video at end
            if (audioTime >= videoDuration) {
              videoElementRef.current.currentTime = videoDuration;
              lastSyncTimeRef.current = now;
              return;
            }

            // If they're significantly out of sync, sync them (higher threshold)
            if (timeDiff > 0.3) {
              videoElementRef.current.currentTime = audioTime;
              lastSyncTimeRef.current = now;
            }
          }
        }}
        ref={audioElementRef}
      />
      {/* Main Content Area */}
      <div className='relative flex h-full w-full overflow-hidden'>
        {/* Back Button - Only show when video is uploaded */}
        {videoFile && <BackButton onClick={resetWidget} />}
        {!videoFile ? (
          // Upload State
          <div className='flex h-full w-full flex-grow flex-col'>
            <div className='flex h-full w-full items-center justify-center p-6 md:p-12'>
              <UploadButton
                loadingVideoSrc={loadingPresetVideoSrc}
                onFileAccepted={handleFileAccepted}
                onPresetSelected={handlePresetVideoSelected}
                showPresets
              />
            </div>
          </div>
        ) : (
          // Two-column layout on desktop, flexible stacked on mobile
          <div className='flex h-full w-full flex-col lg:flex-row'>
            {/* Video Pane */}
            <div className='flex min-h-0 w-full flex-grow flex-col lg:h-full lg:w-1/2'>
              <div className='flex-1 overflow-y-auto p-6 md:p-12'>
                <div className='h-full space-y-16'>
                  <VideoPreview
                    onVideoPause={handleVideoPause}
                    onVideoPlay={handleVideoPlay}
                    onVideoSeeked={handleVideoSeeked}
                    onVideoTimeUpdate={handleVideoTimeUpdate}
                    videoRef={videoElementRef}
                    videoUrl={videoUrl}
                  />
                </div>
              </div>
            </div>

            {/* Controls/CTA Pane */}
            <div className='border-gray-alpha-100 bg-gray-50 relative flex min-h-0 w-full flex-shrink-0 flex-col lg:h-full lg:w-1/2 lg:border-l'>
              {/* Regular Error Display */}
              {error && !isRateLimitError && (
                <div className='mx-6 mt-6 duration-300 animate-in fade-in-0 slide-in-from-bottom-4 md:mx-12 md:mt-12'>
                  <div className='border-red-200 rounded-xl border bg-gradient-to-r from-red-50 to-red-100/50 p-16 shadow-sm md:p-20'>
                    <div className='flex items-start gap-12'>
                      <div className='flex-1'>
                        <p className='f-body-02 leading-relaxed text-red-700 text-sm md:text-base'>{error}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {isProcessing ? (
                // Premium Loading State
                <PremiumLoadingIndicator />
              ) : isRateLimitError ? (
                // Rate Limit State - Styled like success case
                <div className='flex min-h-0 flex-col items-center justify-center p-12 md:p-24 lg:h-full'>
                  <div className='hidden md:block' />
                  <div className='flex w-full flex-col items-center gap-4'>
                    <h3 className='text-gray-900 f-heading-04 mb-4 text-center md:mb-8'>
                      {t("rate_limit_error_title")}
                    </h3>
                  </div>
                  <div className='mt-8 flex flex-col items-center gap-8 text-center md:mt-0 md:gap-12'>
                    <p className='f-body-02 text-gray-600 max-w-sm text-sm'>{error}</p>

                    <Link
                      asButton
                      buttonVariants={{ variant: "primary", size: "lg" }}
                      className='w-full max-w-sm'
                      href={publicUrls.signUp}
                    >
                      {t("signup_cta")}
                    </Link>
                  </div>
                </div>
              ) : hasGeneratedAudio ? (
                // CTA State - Preview is ready
                <div className='flex min-h-0 flex-col items-center justify-center p-12 md:justify-between md:p-24 lg:h-full'>
                  <div className='hidden md:block' />
                  <div className='flex w-full flex-col items-center gap-4'>
                    <h3 className='text-gray-900 f-heading-04 mb-4 text-center md:mb-8'>
                      {t("preview_limitation_title")}
                    </h3>
                    {/* Audio Player - Show only when audio is generated */}
                    {hasGeneratedAudio && (
                      <div className='mt-8 w-full max-w-sm md:mt-16'>
                        <MinimalAudioPlayer
                          audioRef={audioElementRef}
                          onPlay={handlePlay}
                          playingState={playingState}
                        />
                      </div>
                    )}
                  </div>
                  <div className='mt-8 flex flex-col items-center gap-8 text-center md:mt-0 md:gap-12'>
                    <p className='f-body-02 text-gray-600 max-w-sm text-sm'>{t("preview_limitation_description")}</p>

                    <StudioCTAButton className='w-full max-w-sm' />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
